import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, API_BASE } from "../utils/api";

function toImgSrc(img) {
  if (!img) return "";
  const s = String(img).trim();
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("/")) return `${API_BASE}${s}`;
  return s;
}

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ products: [], orders: [], customers: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const modalRef = useRef(null);

  // Keyboard shortcut (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Search on query change
  useEffect(() => {
    if (!query.trim()) {
      setResults({ products: [], orders: [], customers: [] });
      return;
    }

    const delayDebounce = setTimeout(async () => {
      try {
        setLoading(true);
        const result = await apiGet(`/api/shop/search?q=${encodeURIComponent(query)}&limit=5`);
        setResults({
          products: Array.isArray(result?.products) ? result.products : [],
          orders: Array.isArray(result?.orders) ? result.orders : [],
          customers: Array.isArray(result?.customers) ? result.customers : [],
        });
      } catch (err) {
        console.error("Search error:", err);
        setResults({ products: [], orders: [], customers: [] });
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [query]);

  const handleNavigate = (path) => {
    setIsOpen(false);
    setQuery("");
    navigate(path);
  };

  const totalResults = results.products.length + results.orders.length + results.customers.length;

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors text-sm text-gray-600"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <span>Search</span>
        <kbd className="ml-auto hidden md:inline-block px-2 py-0.5 text-xs bg-gray-100 border border-gray-200 rounded">
          ⌘K
        </kbd>
      </button>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100]"
        onClick={() => setIsOpen(false)}
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className="fixed top-20 left-1/2 -translate-x-1/2 w-full max-w-2xl bg-white rounded-xl shadow-2xl border border-gray-200 z-[101]"
      >
        {/* Search Input */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products, orders, customers..."
              className="flex-1 outline-none text-lg"
            />
            {loading && (
              <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
            )}
          </div>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {!query.trim() ? (
            <div className="p-8 text-center text-gray-500 text-sm">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Type to search across products, orders, and customers
            </div>
          ) : totalResults === 0 && !loading ? (
            <div className="p-8 text-center text-gray-500 text-sm">
              <div className="text-4xl mb-2">🔍</div>
              No results found for "{query}"
            </div>
          ) : (
            <div className="p-2">
              {/* Products */}
              {results.products.length > 0 && (
                <div className="mb-4">
                  <div className="px-3 py-2 text-xs font-bold text-gray-500 uppercase">
                    Products ({results.products.length})
                  </div>
                  {results.products.map((product) => {
                    const img = product.image || (Array.isArray(product.images) ? product.images[0] : "");
                    return (
                      <button
                        key={product._id}
                        onClick={() => handleNavigate("/shop/products")}
                        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 text-left"
                      >
                        <div className="w-10 h-10 rounded-lg border border-gray-200 bg-gray-50 overflow-hidden flex-shrink-0">
                          {img ? (
                            <img src={toImgSrc(img)} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">
                              📦
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm truncate">{product.title || product.name}</div>
                          <div className="text-xs text-gray-500">${Number(product.price || 0).toFixed(2)}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Orders */}
              {results.orders.length > 0 && (
                <div className="mb-4">
                  <div className="px-3 py-2 text-xs font-bold text-gray-500 uppercase">
                    Orders ({results.orders.length})
                  </div>
                  {results.orders.map((order) => (
                    <button
                      key={order._id}
                      onClick={() => handleNavigate("/shop/orders")}
                      className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 text-left"
                    >
                      <div>
                        <div className="font-semibold text-sm">
                          Order #{String(order._id).slice(-8)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {order.user?.name || "Customer"} • {order.status}
                        </div>
                      </div>
                      <div className="text-sm font-bold">${Number(order.total || 0).toFixed(2)}</div>
                    </button>
                  ))}
                </div>
              )}

              {/* Customers */}
              {results.customers.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-bold text-gray-500 uppercase">
                    Customers ({results.customers.length})
                  </div>
                  {results.customers.map((customer) => (
                    <button
                      key={customer._id}
                      onClick={() => handleNavigate("/shop/customers")}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 text-left"
                    >
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-blue-400 flex items-center justify-center text-white font-bold flex-shrink-0">
                        {(customer.name || customer.email || "?").charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{customer.name || "Anonymous"}</div>
                        <div className="text-xs text-gray-500 truncate">{customer.email}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-4">
              <span>Press <kbd className="px-2 py-0.5 bg-white border border-gray-200 rounded">ESC</kbd> to close</span>
            </div>
            <span>Tip: Use ⌘K to open search anywhere</span>
          </div>
        </div>
      </div>
    </>
  );
}
